// src/hr/portal/PolicyDrawers.js -- the Policies page's drawers.
//   PolicyDrawer: add a policy (a PDF or an https link -- exactly one), or a new version of one. Title,
//   everyone or specific people, the required-notice flag (always everyone, including future hires) and
//   the days people have to acknowledge it. A PDF goes up as multipart (field `file`); a link as JSON.
//   The server's refusals ('Upload a PDF up to 25 MB', 'Links must start with https://' ...) stay inline.
//   PolicyStatusDrawer: the versions, who signed this version and when, and who is still outstanding
//   with their due date (from the server -- R30) and an overdue badge.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiAlertCircle, FiCheck, FiFileText, FiLink, FiSearch, FiUpload } from 'react-icons/fi';
import { hr, errorText } from '../api';
import { Avatar, Btn, Drawer, EmptyState, Field, Pill, Seg, Skeleton } from '../kit';
import { fullDate, fullName, isCurrent, stampLabel } from './portalUtils';
import { dateOfTimestamp } from '../timeoff/dates';
import { FormError } from './FormError';

const MAX_PDF_BYTES = 25 * 1024 * 1024;
const sizeText = (bytes) => (bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`);

function PeoplePicker({ people, selected, onChange, error }) {
  const [query, setQuery] = useState('');
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const shown = people.filter((p) => {
    const hay = [fullName(p), p.email, p.department].filter(Boolean).join(' ').toLowerCase();
    return words.every((w) => hay.includes(w));
  });
  const toggle = (id) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  return (
    <fieldset className="hrp-pick" aria-invalid={error ? 'true' : undefined}>
      <legend className="hrp-field__label">
        People
        <span className="hrp-field__note">{`${selected.length} selected`}</span>
      </legend>
      <Field label="Find someone" hideLabel type="search" icon={<FiSearch />} placeholder="Find someone" value={query} onChange={(e) => setQuery(e.target.value)} />
      <ul className="hrp-pick__list">
        {shown.map((p) => (
          <li key={p.id}>
            <label className="hrp-pick__it">
              <input type="checkbox" checked={selected.includes(p.id)} onChange={() => toggle(p.id)} />
              <Avatar id={p.id} name={fullName(p)} size="sm" />
              <span className="hrp-pick__nm">{fullName(p)}</span>
              {p.department && <em>{p.department}</em>}
            </label>
          </li>
        ))}
        {shown.length === 0 && <li className="hrp-pick__none">No one matches.</li>}
      </ul>
      {error && <div className="hrp-field__err" role="alert">{error}</div>}
    </fieldset>
  );
}

// policy: null to add one, or the latest version to follow with a new one.
function PolicyForm({ policy, people, onClose, onDone }) {
  const versioning = !!policy;
  const fileRef = useRef(null);
  const [source, setSource] = useState(versioning ? 'keep' : 'file');
  const [file, setFile] = useState(null);
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState(policy ? policy.title : '');
  const [required, setRequired] = useState(!!(policy && policy.isRequiredNotice));
  const [assignTo, setAssignTo] = useState(policy && policy.assignTo === 'list' ? 'list' : 'all');
  const [assignees, setAssignees] = useState(policy ? [...(policy.assigneeIds || [])] : []);
  const [dueDays, setDueDays] = useState(String(policy && Number.isFinite(policy.dueDays) ? policy.dueDays : 14));
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const active = useMemo(() => (people || []).filter(isCurrent).sort((a, b) => fullName(a).localeCompare(fullName(b))), [people]);
  const target = required ? 'all' : assignTo;
  const sources = versioning
    ? [{ value: 'keep', label: policy.url ? 'Same link' : 'Same PDF' }, { value: 'file', label: 'New PDF' }, { value: 'link', label: 'New link' }]
    : [{ value: 'file', label: 'PDF' }, { value: 'link', label: 'Link' }];
  const clear = (key) => { setErrors((x) => ({ ...x, [key]: undefined })); setError(''); };

  const pickFile = (e) => {
    const chosen = e.target.files && e.target.files[0];
    setFile(chosen || null);
    clear('file');
    if (chosen && chosen.size > MAX_PDF_BYTES) setErrors((x) => ({ ...x, file: 'Upload a PDF up to 25 MB' }));
  };

  const submit = async () => {
    if (busy) return;
    const found = {};
    if (!title.trim()) found.title = 'Give the policy a title';
    if (source === 'file' && !file) found.file = 'Choose a PDF';
    if (source === 'file' && file && file.size > MAX_PDF_BYTES) found.file = 'Upload a PDF up to 25 MB';
    if (source === 'link' && !url.trim()) found.url = 'Add a link that starts with https://';
    if (target === 'list' && assignees.length === 0) found.assignees = 'Pick at least one person';
    setErrors(found);
    if (Object.keys(found).length) return;

    const due = dueDays.trim();
    const dueValue = due !== '' && Number.isFinite(Number(due)) ? Number(due) : due;
    let body;
    if (source === 'file') {
      body = new FormData();
      body.append('title', title.trim());
      body.append('assignTo', target);
      if (target === 'list') assignees.forEach((id) => body.append('assigneeIds', id));
      body.append('isRequiredNotice', required ? 'true' : 'false');
      body.append('dueDays', due);
      body.append('file', file);
    } else {
      body = { title: title.trim(), assignTo: target, isRequiredNotice: required, dueDays: dueValue };
      if (source === 'link') body.url = url.trim();
      if (target === 'list') body.assigneeIds = assignees;
    }

    setBusy(true);
    setError('');
    try {
      const result = versioning ? await hr.admin.policies.newVersion(policy.id, body) : await hr.admin.policies.create(body);
      const saved = result && result.policy;
      onDone(versioning
        ? `${title.trim()} v${saved ? saved.version : policy.version + 1} is out — everyone it's for is asked to sign again`
        : `${title.trim()} added`);
    } catch (err) {
      setError(errorText(err, "Couldn't save the policy. Try again."));
      setBusy(false);
    }
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title={versioning ? 'New version' : 'Upload policy'}
      subtitle={versioning
        ? `${policy.title} v${policy.version + 1}. Everyone is asked to sign the new version; v${policy.version} acknowledgments are kept as a record.`
        : 'A PDF or an https link. Everyone it is for is asked to read it and click “I’ve read this”.'}
      width={460}
      onSubmit={submit}
      className="hrp-pold"
      footer={(
        <>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" icon={FiUpload} loading={busy}>{versioning ? 'Upload version' : 'Upload policy'}</Btn>
        </>
      )}
    >
      <div className="hrp-field">
        <span className="hrp-field__label" id="hrp-pold-src">Source</span>
        <Seg labelledBy="hrp-pold-src" options={sources} value={source} onChange={(v) => { setSource(v); clear('file'); clear('url'); }} />
      </div>
      {source === 'file' && (
        <Field label="PDF file" error={errors.file} hint={file ? `${file.name} · ${sizeText(file.size)}` : 'PDF only, up to 25 MB.'}>
          <input ref={fileRef} type="file" className="hrp-input hrp-pold__file" accept="application/pdf,.pdf" onChange={pickFile} />
        </Field>
      )}
      {source === 'link' && (
        <Field label="Link" type="url" icon={<FiLink />} value={url} placeholder="https://" error={errors.url}
          onChange={(e) => { setUrl(e.target.value); clear('url'); }} hint="Must start with https://" />
      )}
      {source === 'keep' && (
        <p className="hrp-pold__keep"><FiFileText aria-hidden="true" />{policy.url ? 'The new version keeps the same link.' : 'The new version keeps the same PDF.'}</p>
      )}

      <Field label="Title" value={title} maxLength={120} error={errors.title} onChange={(e) => { setTitle(e.target.value); clear('title'); }} />

      <div className="hrp-pold__req">
        <div className="hrp-set__captxt">
          <b id="hrp-pold-req">Required notice</b>
          <span id="hrp-pold-req-hint">Goes to everyone, including future hires. HR re-issues required notices every December.</span>
        </div>
        <button type="button" className="hrp-switch hrp-switch--accent" role="switch" aria-checked={required} aria-labelledby="hrp-pold-req" aria-describedby="hrp-pold-req-hint"
          onClick={() => { setRequired((v) => !v); clear('assignees'); }}>
          <span className="hrp-switch__knob" aria-hidden="true" />
        </button>
      </div>

      <div className="hrp-field">
        <span className="hrp-field__label" id="hrp-pold-who">Assign to</span>
        <Seg
          labelledBy="hrp-pold-who"
          options={[{ value: 'all', label: 'Everyone' }, { value: 'list', label: 'Specific people', disabled: required }]}
          value={target}
          onChange={(v) => { setAssignTo(v); clear('assignees'); }}
        />
        <div className="hrp-field__hint">
          {required ? 'Required notices always go to everyone.' : target === 'all' ? 'Everyone active now, and everyone added later.' : 'Only the people you pick.'}
        </div>
      </div>
      {target === 'list' && <PeoplePicker people={active} selected={assignees} onChange={(next) => { setAssignees(next); clear('assignees'); }} error={errors.assignees} />}

      <Field
        label="Days to acknowledge"
        type="number"
        min={1}
        max={90}
        step={1}
        inputMode="numeric"
        value={dueDays}
        onChange={(e) => { setDueDays(e.target.value); setError(''); }}
        hint="1–90. Counted from the upload day, or a new hire's start date if later. Reminders go out 7 days before and on the day."
        className="hrp-pold__due"
      />

      <FormError>{error}</FormError>
    </Drawer>
  );
}

export function PolicyDrawer({ open, ...props }) {
  if (!open) return null;
  return <PolicyForm {...props} />;
}

// ---------------------------------------------------------------------------------------------
// Status
// ---------------------------------------------------------------------------------------------

function PolicyStatus({ policy, onClose }) {
  const [state, setState] = useState({ status: 'loading', data: null });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let live = true;
    setState({ status: 'loading', data: null });
    hr.admin.policies.status(policy.id)
      .then((data) => { if (live) setState({ status: 'ready', data: data || {} }); })
      .catch((err) => { if (live) setState({ status: 'error', data: null, error: errorText(err, "Couldn't load who has signed.") }); });
    return () => { live = false; };
  }, [policy.id, attempt]);

  const data = state.data || {};
  const signed = data.signed || [];
  const outstanding = data.outstanding || [];
  const versions = data.versions || [];
  const overdue = outstanding.filter((o) => o.overdue).length;

  return (
    <Drawer
      open
      onClose={onClose}
      title={policy.title}
      subtitle={`v${policy.version} · ${policy.signedCount} of ${policy.assignedCount} signed${overdue ? ` · ${overdue} overdue` : ''}`}
      width={440}
      className="hrp-pols"
      footer={<Btn variant="ghost" onClick={onClose}>Close</Btn>}
    >
      {state.status === 'loading' && <Skeleton label="Loading who has signed" lines={6} height={16} />}
      {state.status === 'error' && (
        <EmptyState compact icon={FiAlertCircle} title="Couldn't load the status" action={<Btn variant="ghost" size="sm" onClick={() => setAttempt((n) => n + 1)}>Try again</Btn>}>
          {state.error}
        </EmptyState>
      )}
      {state.status === 'ready' && (
        <>
          <section className="hrp-pols__sec">
            <h3 className="hrp-group">{`Not signed yet (${outstanding.length})`}</h3>
            {outstanding.length === 0 ? <p className="hrp-pols__none"><FiCheck aria-hidden="true" />Everyone has signed this version.</p> : (
              <ul className="hrp-pols__list" aria-label="Not signed yet">
                {outstanding.map((o) => (
                  <li key={o.employeeId} className="hrp-pols__it">
                    <Avatar id={o.employeeId} name={o.name} size="sm" />
                    <span className="hrp-pols__nm">{o.name}</span>
                    {o.overdue
                      ? <Pill tone="bad">{`Overdue · was due ${fullDate(o.dueDate)}`}</Pill>
                      : <span className="hrp-pols__when">{o.dueDate ? `Due ${fullDate(o.dueDate)}` : 'No due date'}</span>}
                  </li>
                ))}
              </ul>
            )}
          </section>
          <section className="hrp-pols__sec">
            <h3 className="hrp-group">{`Signed (${signed.length})`}</h3>
            {signed.length === 0 ? <p className="hrp-pols__none">Nobody has signed this version yet.</p> : (
              <ul className="hrp-pols__list" aria-label="Signed">
                {signed.map((s) => (
                  <li key={s.employeeId} className="hrp-pols__it">
                    <Avatar id={s.employeeId} name={s.name} size="sm" />
                    <span className="hrp-pols__nm">{s.name}</span>
                    <span className="hrp-pols__when">{`v${s.version} · ${s.ackAt ? stampLabel(s.ackAt) : 'signed'}`}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
          {versions.length > 0 && (
            <section className="hrp-pols__sec">
              <h3 className="hrp-group">Versions</h3>
              <ul className="hrp-pols__list" aria-label="Versions">
                {versions.map((v) => (
                  <li key={v.id} className="hrp-pols__it">
                    <span className="hrp-pols__v">{`v${v.version}`}</span>
                    <span className="hrp-pols__nm">{v.uploadedAt ? `Uploaded ${fullDate(dateOfTimestamp(v.uploadedAt))}` : 'Uploaded'}</span>
                    {v.version === policy.version ? <Pill tone="ok">Current</Pill> : <span className="hrp-pols__when">Acknowledgments kept</span>}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </Drawer>
  );
}

export function PolicyStatusDrawer({ policy, ...props }) {
  if (!policy) return null;
  return <PolicyStatus policy={policy} {...props} />;
}
